<template>
  <div class="allProducts">
    <div class="allProducts__header p-8">
      <h1
        class="h1--underline"
      >
        {{ $tv('allProducts', 'categories') }}
      </h1>
    </div>
    <div
      v-if="isLoading"
      class="allProducts--loading"
    >
      <AppLoadingIndicator />
    </div>
    <div
      v-if="noCategoriesFound"
      class="p-8"
    >
      {{ $tv('noProductsFound', 'categories') }}
    </div>
    <div
      v-for="(topCategory, index) in categoriesHirarchy"
      :key="index"
      class="allProducts__categories"
      v-show="true"
    >
      <div
        v-for="(groupCategory, groupIndex) in topCategory?.subcategories"
        :key="groupIndex"
      >
        <div class="allProducts__categories__header p-8">
          <div>
            <h2>
              {{ topCategory.shortName + ' / ' + groupCategory.shortName }}
            </h2>
          </div>
          <div :class="brandClass(groupCategory)">
            <nuxt-link
              class="k-button k-button-md k-button-solid k-button-solid-primary"
              :to="groupCategory.url"
            >
              {{ $tv('showAll', 'categories') }}
            </nuxt-link>
          </div>
        </div>
        <CategoryListNew
          :categories="groupCategory?.subcategories"
          :path="groupCategory.url"
          :lazy="true"
          :is-all-categories="true"
        />
        <div class="break-section">
          <div class="break-line" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {CATEGORIES} from '~/assets/js/constants'
import {mapActions, storeToRefs} from 'pinia'
import {useUserStore} from '@/store/user'
import AppLoadingIndicator from '~/components/loading-indicators/app-loading-indicator'
import CategoryListNew from '~/components/category/CategoryListNew'
import {useBreadcrumbStore} from '~/store/breadcrumb'
import useI18nExtended from '~/composables/usei18nExtended'
import usePim from '~/composables/pim_json_loader'

export default defineComponent({
  name: 'Categories',
  components: {AppLoadingIndicator, CategoryListNew},
  async setup() {
    const i18n = useI18nExtended()
    const {showInchValues, settings} = storeToRefs(useUserStore())
    const nuxtApp = useNuxtApp()
    const currentRegion = nuxtApp.$globalization.getRegion()?.toLowerCase() || 'na'
    const currentLocale = i18n.locale.value || 'en'

    const categoriesHirarchy = await usePim(`v1/${currentRegion}/${currentLocale}/categories_hirarchy.json`)

    const {setPaths} = useBreadcrumbStore()
    setPaths(
      [
        {
          label: i18n.tv('category', 'breadcrumbs'),
          path: '/categories'
        }
      ]
    )
    return {
      showInchValues,
      userSettings: settings,
      categoriesHirarchy
    }
  },
  head() {
    const title = this.$tv('allProductsTitle', 'seo', 'All Sealing Products | Freudenberg FST')
    const description = this.$tv('allProductsDescription', 'seo', 'Find our complete Sealing Catalogue including hydraulic seals, o-rings, radial shaft seals and much more.')

    return {
      title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: description
        }
      ]
    }
  },
  data() {
    return {
      rootCategoriesUrl: CATEGORIES.RootCategoriesUrl,
      categoryCount: 10,
      loadMoreTimeout: null,
      isLoading: false,
      initialDataLoaded: false
    }
  },
  computed: {
    noCategoriesFound() {
      return this.initialDataLoaded && !this.isLoading && this.categoriesHirarchy?.length === 0
    }
  },
  async mounted() {
    const that = this
    const loadingTimeout = setTimeout(() => {
      that.isLoading = true
    }, 150)

    this.loadMore()

    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
    }
    this.isLoading = false
    this.initialDataLoaded = true
  },
  beforeUnmount() {
    clearTimeout(this.loadMoreTimeout)
  },
  watch: {
    allCategories() {
      this.categoryCount = 10
      this.loadMore()
    }
  },
  methods: {
    ...mapActions(useUserStore, ['updateSettings']),
    loadMore() {
      if (this.categoryCount > this.allProducts?.length) {
        return
      }
      this.categoryCount += 5
      this.$nextTick(() => this.loadMoreTimeout = setTimeout(this.loadMore, 50))
    },
    brandClass(category) {
      if (category.brands) {
        let brandCodes = category.brands.map(b => b.substring(0, 1).toLowerCase())?.sort()
        return ` brand-${brandCodes.join('')}`
      }
      return null
    }
  }
})
</script>
<style lang="scss">
.allProducts {
  &__categories {
    &__header {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;
      align-items: center;
      background-color: $color__fst__blue__010;

      @include breakpoint-down($md) {
        flex-direction: column;
        align-items: normal;

        .k-button {
          margin-bottom: $default-margin-padding-width;
        }
      }
    }

    .break-section {
      background-color: $color__fst__blue__010;
      padding: 1rem 2rem;

      .break-line {
        background-color: $color__fst__gray__030;
        height: 0.0625rem;
        margin: 0;
      }
    }
  }

  &__header {
    display: flex;
    position: relative;
    justify-content: space-between;
    align-items: center;

    @include breakpoint-down($sm) {
      align-items: flex-start;
      flex-direction: column;
    }
  }

  &_inchfilter {
    display: flex;
    width: 12rem;

    .switch .label {
      width: 8.25rem;
    }

    @include breakpoint-down($sm) {
      padding-top: 1rem;
    }
  }

  &--loading {
    display: flex;
    justify-content: center;
    margin-bottom: 3rem;
  }

  .p-8 {
    padding: 0 2rem 1rem
  }

  h1 {
    margin-top: .2rem;
  }
}
</style>
